import type { ClientToolName } from "@salon/types/ai-agent";
import { useEffect } from "react";
import type { z } from "zod";
import { useClientToolsStore } from "../store/client-tools-store";

/**
 * Helper function to convert Zod schema to JSON Schema format
 * This is a simplified conversion that handles common Zod types
 */
function convertZodToJsonSchema(schema: z.ZodObject<any>): Record<string, any> {
  const zodDef = (schema as any)._def;

  if (zodDef?.typeName !== "ZodObject") {
    return { type: "object", properties: {}, required: [] };
  }

  const shape = zodDef.shape?.();
  if (!shape) {
    return { type: "object", properties: {}, required: [] };
  }

  const properties: Record<string, any> = {};
  const required: string[] = [];

  Object.entries(shape).forEach(([key, value]: [string, any]) => {
    const fieldSchema = value as any;
    const typeName = fieldSchema._def?.typeName;

    const propertyDef: Record<string, any> = {
      type: getZodType(fieldSchema),
      description: fieldSchema.description || "",
    };

    // Extract enum values if this is a ZodEnum
    if (typeName === "ZodEnum" || fieldSchema._def?.innerType?._def?.typeName === "ZodEnum") {
      const enumSchema = typeName === "ZodEnum" ? fieldSchema : fieldSchema._def.innerType;
      const enumValues = enumSchema._def?.values;
      if (enumValues) {
        propertyDef.enum = enumValues;
      }
    }

    properties[key] = propertyDef;

    // Check if field is required (not optional)
    if (!fieldSchema.isOptional?.()) {
      required.push(key);
    }
  });

  return {
    type: "object",
    properties,
    required: required.length > 0 ? required : undefined,
  };
}

/**
 * Helper to extract basic type from Zod schema
 */
function getZodType(schema: any): string {
  const typeName = schema._def?.typeName;

  switch (typeName) {
    case "ZodString":
      return "string";
    case "ZodNumber":
      return "number";
    case "ZodBoolean":
      return "boolean";
    case "ZodEnum":
      return "string";
    case "ZodArray":
      return "array";
    case "ZodObject":
      return "object";
    case "ZodOptional":
      return getZodType(schema._def.innerType);
    default:
      return "string";
  }
}

interface UseClientToolProps {
  toolName: ClientToolName;
  description: string;
  parameters: z.ZodObject<any>;
  execute: (args: Record<string, any>) => Promise<any> | any;
}

/**
 * Hook to register a client-side tool
 *
 * This hook provides a clean way to declare tools that will be sent to the backend
 * and executed on the client when the AI agent requests them.
 *
 * @example
 * ```ts
 * useClientTool({
 *   toolName: "getDetailedViewContext",
 *   description: "Get detailed information about what the user is viewing",
 *   parameters: z.object({}),
 *   execute: async () => {
 *     return { success: true, context: "..." };
 *   },
 * });
 * ```
 */
export function useClientTool({ toolName, description, parameters, execute }: UseClientToolProps) {
  const registerTool = useClientToolsStore((state) => state.registerTool);
  const unregisterTool = useClientToolsStore((state) => state.unregisterTool);

  useEffect(() => {
    // Convert Zod schema to JSON Schema and register
    const jsonSchema = convertZodToJsonSchema(parameters);
    registerTool(toolName, description, jsonSchema, execute);

    // Cleanup: unregister tool on unmount
    return () => {
      unregisterTool(toolName);
    };
  }, [toolName, description, parameters, execute, registerTool, unregisterTool]);
}
